import { legendSamples } from '../../../composition/legendSamples';
import { deriveLegendEntries } from '../../../styles/legend';
import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import { Icon } from '../../../components/Icon';
import type { VectorSourceDescriptor } from '../../../data/model';
import type { LayerDefinition } from '../../../project/model';
import {
  categorisableFields,
  categoryLabel,
  DEFAULT_LINE_WIDTH_RANGE,
  DEFAULT_POINT_SIZE_RANGE,
  MAX_CATEGORISED_VALUES,
  numericClassesForClassification,
  numericFields,
} from '../../../styles/classification';
import { effectiveSymbols } from '../../../styles/effectiveSymbols';
import { geometryFamiliesForSource, type GeometryFamily } from '../../../styles/geometry';
import {
  createGraduatedBoundaryFormatter,
  formatGraduatedNumber,
  graduatedClassLabel,
} from '../../../styles/legend';
import type {
  CategorisedClassification,
  GraduatedClassification,
  GraduatedGeneratedMethod,
  GraduatedVisualVariable,
  ThematicSymbolDefinition,
} from '../../../styles/model';
import {
  CATEGORICAL_PALETTES,
  GRADUATED_PALETTE_GROUPS,
  type PaletteId,
} from '../../../styles/palettes';
import type { StyleChangeResult } from '../../state/appStore';
import { useAppStore } from '../../state/useAppStore';
import { AnchoredMenu } from '../AnchoredMenu';
import { NumericControl } from './NumericControl';
import { fieldDisplayLabel } from '../../../joins/effectiveLayer';
import { SymbolOverrideEditor } from './SymbolOverrideEditor';
import { SymbolPreview } from './SymbolPreview';

interface ThematicControlsProps {
  readonly classification: CategorisedClassification | GraduatedClassification;
  readonly layer: LayerDefinition;
  readonly onResult: (result: StyleChangeResult) => void;
  readonly source: VectorSourceDescriptor;
}

function PalettePreview({ colours }: { readonly colours: readonly string[] }) {
  return (
    <span aria-hidden="true" className="palette-preview" data-palette-colours={colours.join(',')}>
      {colours.slice(0, 8).map((colour, index) => (
        <span key={`${colour}-${index}`} style={{ backgroundColor: colour }} />
      ))}
    </span>
  );
}

interface PaletteChoice {
  readonly colours: readonly string[];
  readonly id: PaletteId;
  readonly name: string;
}

interface PaletteChoiceGroup {
  readonly kind: string;
  readonly name: string;
  readonly palettes: readonly PaletteChoice[];
}

function PalettePicker({
  groups,
  onSelect,
  selectedColours,
  selectedId,
}: {
  readonly groups: readonly PaletteChoiceGroup[];
  readonly onSelect: (paletteId: PaletteId) => void;
  readonly selectedColours: readonly string[];
  readonly selectedId: PaletteId;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const selected = groups
    .flatMap((group) => group.palettes)
    .find((palette) => palette.id === selectedId);
  const selectedName = selected?.name ?? 'Unknown';
  const labelId = `palette-picker-label-${selectedId}`;
  const valueId = `palette-picker-value-${selectedId}`;

  return (
    <div className="palette-picker">
      <span className="style-field-label" id={labelId}>
        Palette
      </span>
      <button
        aria-expanded={open}
        aria-haspopup="menu"
        aria-labelledby={`${labelId} ${valueId}`}
        className="palette-picker-trigger"
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
            event.preventDefault();
            setOpen(true);
          }
        }}
        ref={triggerRef}
        type="button"
      >
        <span className="palette-picker-name" id={valueId}>
          {selectedName}
        </span>
        <PalettePreview colours={selectedColours} />
        <span aria-hidden="true" className="palette-picker-chevron">
          ▾
        </span>
      </button>
      <AnchoredMenu
        anchorRef={triggerRef}
        className="palette-picker-menu"
        label="Palette choices"
        onDismiss={() => setOpen(false)}
        open={open}
      >
        {groups.map((group) => (
          <div className="palette-picker-group" key={group.kind}>
            <div className="palette-picker-group-label">{group.name}</div>
            {group.palettes.map((palette) => (
              <button
                aria-checked={palette.id === selectedId}
                data-palette-id={palette.id}
                key={palette.id}
                onClick={() => {
                  onSelect(palette.id);
                  setOpen(false);
                }}
                role="menuitemradio"
                type="button"
              >
                <PalettePreview colours={palette.colours} />
                <span>{palette.name}</span>
              </button>
            ))}
          </div>
        ))}
      </AnchoredMenu>
    </div>
  );
}

function previewGeometry(families: readonly GeometryFamily[]): GeometryFamily | 'mixed' {
  return families.length === 1 ? (families[0] ?? 'point') : 'mixed';
}

function BoundaryControl({
  format,
  index,
  layerId,
  onCommit,
  value,
}: {
  readonly format: (value: number) => string;
  readonly index: number;
  readonly layerId: string;
  readonly onCommit: (value: number) => boolean;
  readonly value: number;
}) {
  const [draft, setDraft] = useState(format(value));
  const cancelledRef = useRef(false);
  const dirtyRef = useRef(false);
  const commit = (inputValue: string) => {
    if (cancelledRef.current) {
      cancelledRef.current = false;
      dirtyRef.current = false;
      setDraft(format(value));
      return;
    }
    if (!dirtyRef.current && inputValue === String(value)) {
      setDraft(format(value));
      return;
    }
    const numeric = inputValue.trim() === '' ? Number.NaN : Number(inputValue);
    if (!Number.isFinite(numeric) || !onCommit(numeric)) {
      dirtyRef.current = false;
      setDraft(format(value));
      return;
    }
    dirtyRef.current = false;
    setDraft(format(numeric));
  };
  return (
    <input
      aria-label={`Boundary ${index} value`}
      className="class-boundary-input"
      id={`class-boundary-${layerId}-${index}`}
      onBlur={(event) => commit(event.currentTarget.value)}
      onChange={(event) => {
        dirtyRef.current = true;
        setDraft(event.target.value);
      }}
      onFocus={(event) => {
        dirtyRef.current = false;
        flushSync(() => setDraft(String(value)));
        event.currentTarget.select();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          commit(event.currentTarget.value);
          event.currentTarget.blur();
        }
        if (event.key === 'Escape') {
          cancelledRef.current = true;
          setDraft(format(value));
          event.currentTarget.blur();
        }
      }}
      inputMode="decimal"
      type="text"
      value={draft}
    />
  );
}

function graduatedVariationValue(variable: GraduatedVisualVariable): string {
  if (variable.kind === 'colour') return 'colour';
  if (variable.kind === 'point-size') return variable.includeColour ? 'colour-size' : 'size';
  return variable.includeColour ? 'colour-width' : 'width';
}

function variationForSelection(
  selection: string,
  current: GraduatedVisualVariable,
): GraduatedVisualVariable {
  if (selection === 'colour') return { kind: 'colour' };
  if (selection === 'size' || selection === 'colour-size') {
    return {
      kind: 'point-size',
      includeColour: selection === 'colour-size',
      minimum: current.kind === 'point-size' ? current.minimum : DEFAULT_POINT_SIZE_RANGE.minimum,
      maximum: current.kind === 'point-size' ? current.maximum : DEFAULT_POINT_SIZE_RANGE.maximum,
    };
  }
  return {
    kind: 'line-width',
    includeColour: selection === 'colour-width',
    minimum: current.kind === 'line-width' ? current.minimum : DEFAULT_LINE_WIDTH_RANGE.minimum,
    maximum: current.kind === 'line-width' ? current.maximum : DEFAULT_LINE_WIDTH_RANGE.maximum,
  };
}

function colourVariationActive(classification: GraduatedClassification): boolean {
  return (
    classification.visualVariable.kind === 'colour' || classification.visualVariable.includeColour
  );
}

export function ThematicControls({
  classification,
  layer,
  onResult,
  source,
}: ThematicControlsProps) {
  const [expandedEntry, setExpandedEntry] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState<string | null>(null);
  const [labelDraft, setLabelDraft] = useState('');
  const setClassificationField = useAppStore((state) => state.setLayerClassificationField);
  const setClassCount = useAppStore((state) => state.setLayerGraduatedClassCount);
  const setGraduatedBoundary = useAppStore((state) => state.setLayerGraduatedBoundary);
  const setGraduatedMethod = useAppStore((state) => state.setLayerGraduatedMethod);
  const setGraduatedVisualVariable = useAppStore((state) => state.setLayerGraduatedVisualVariable);
  const setGraduatedPaletteReversed = useAppStore(
    (state) => state.setLayerGraduatedPaletteReversed,
  );
  const resetGraduatedBreaks = useAppStore((state) => state.resetLayerGraduatedBreaks);
  const setPalette = useAppStore((state) => state.setLayerPalette);
  const updateCategoryOverride = useAppStore((state) => state.updateLayerCategoryOverride);
  const resetCategoryOverride = useAppStore((state) => state.resetLayerCategoryOverride);
  const updateClassOverride = useAppStore((state) => state.updateLayerClassOverride);
  const resetClassOverride = useAppStore((state) => state.resetLayerClassOverride);
  const setLegendLabel = useAppStore((state) => state.setLayerThematicLegendLabel);
  const fields =
    classification.mode === 'categorised'
      ? categorisableFields(source.fields)
      : numericFields(source.fields);
  const families = geometryFamiliesForSource(source);
  const geometry = previewGeometry(families);
  const samples = legendSamples({
    layerId: layer.id,
    name: layer.name,
    headingEnabled: false,
    geometry,
    entries: deriveLegendEntries(layer.name, layer.style),
  });
  const thematicSymbols =
    classification.mode === 'categorised'
      ? classification.categories.map((category) => category.symbol)
      : classification.classes.map((numericClass) => numericClass.symbol);
  const thematicColours = thematicSymbols.flatMap((symbol) =>
    symbol.generated.colour ? [symbol.generated.colour] : [],
  );
  const sizeVariable =
    classification.mode === 'graduated' && classification.visualVariable.kind !== 'colour'
      ? classification.visualVariable
      : null;
  const numericClasses =
    classification.mode === 'graduated' ? numericClassesForClassification(classification) : [];
  const formatBoundary =
    classification.mode === 'graduated'
      ? createGraduatedBoundaryFormatter(classification.boundaries)
      : (value: number) => String(value);
  const paletteGroups: readonly PaletteChoiceGroup[] =
    classification.mode === 'categorised'
      ? [{ kind: 'categorical', name: 'Categorical', palettes: CATEGORICAL_PALETTES }]
      : GRADUATED_PALETTE_GROUPS;

  const closeLabelEditor = (form: HTMLFormElement) => {
    setEditingLabel(null);
    form.parentElement?.querySelector<HTMLButtonElement>('.thematic-label-edit')?.focus();
  };

  const renderLabelEditor = (
    key: string,
    generatedLabel: string,
    legendLabel: string | undefined,
    entry: number | 'other' | 'null',
  ) => (
    <>
      <button
        aria-label={`Edit legend label for ${generatedLabel}`}
        className="icon-button thematic-label-edit"
        onClick={() => {
          setEditingLabel(editingLabel === key ? null : key);
          setLabelDraft(legendLabel ?? '');
        }}
        type="button"
      >
        <Icon name="rename" />
      </button>
      {editingLabel === key ? (
        <form
          className="thematic-label-form"
          onKeyDown={(event) => {
            if (event.key !== 'Escape') return;
            event.preventDefault();
            event.stopPropagation();
            closeLabelEditor(event.currentTarget);
          }}
          onSubmit={(event) => {
            event.preventDefault();
            const result = setLegendLabel(layer.id, entry, labelDraft);
            onResult(result);
            if (result.ok) closeLabelEditor(event.currentTarget);
          }}
        >
          <label htmlFor={`legend-label-${layer.id}-${key}`}>Legend label</label>
          <input
            autoFocus
            id={`legend-label-${layer.id}-${key}`}
            maxLength={120}
            onChange={(event) => setLabelDraft(event.target.value)}
            placeholder={generatedLabel}
            type="text"
            value={labelDraft}
          />
          <button className="secondary-button" type="submit">
            Apply
          </button>
          {legendLabel ? (
            <button
              className="secondary-button"
              onClick={(event) => {
                onResult(setLegendLabel(layer.id, entry, ''));
                if (event.currentTarget.form) closeLabelEditor(event.currentTarget.form);
              }}
              type="button"
            >
              Reset
            </button>
          ) : null}
          <button
            aria-label="Cancel legend label edit"
            className="icon-button"
            onClick={(event) => {
              if (event.currentTarget.form) closeLabelEditor(event.currentTarget.form);
            }}
            title="Cancel"
            type="button"
          >
            <Icon name="close" />
          </button>
        </form>
      ) : null}
    </>
  );

  const renderEntry = (
    key: string,
    generatedLabel: string,
    symbol: ThematicSymbolDefinition,
    entry: number | 'other' | 'null',
    change: Parameters<typeof SymbolOverrideEditor>[0]['onChange'],
    reset: () => void,
  ) => {
    const isExpanded = expandedEntry === key;
    const symbols = effectiveSymbols(layer.style.symbols, symbol);
    return (
      <div className={`thematic-entry${isExpanded ? ' thematic-entry-expanded' : ''}`} key={key}>
        <div className="thematic-row">
          <button
            aria-expanded={isExpanded}
            aria-label={`Edit symbol for ${symbol.legendLabel ?? generatedLabel}`}
            className="symbol-preview-button"
            onClick={() => setExpandedEntry(isExpanded ? null : key)}
            type="button"
          >
            <SymbolPreview
              geometry={geometry}
              symbols={symbols}
              sample={typeof entry === 'number' ? samples[entry]! : samples.at(-1)!}
            />
          </button>
          <span className="thematic-label-block">
            <span className="thematic-label" title={symbol.legendLabel ?? generatedLabel}>
              {symbol.legendLabel ?? generatedLabel}
            </span>
            {symbol.legendLabel ? <small>{generatedLabel}</small> : null}
          </span>
          {renderLabelEditor(key, generatedLabel, symbol.legendLabel, entry)}
        </div>
        {isExpanded ? (
          <SymbolOverrideEditor
            families={families}
            target={{ kind: 'point', layerId: layer.id, entry }}
            id={`${layer.id}-${key}`}
            onChange={change}
            onReset={reset}
            symbols={symbols}
          />
        ) : null}
      </div>
    );
  };

  const renderGraduatedEntry = (index: number, numericClass: (typeof numericClasses)[number]) => {
    const key = `class-${index}`;
    const label = graduatedClassLabel(
      numericClass.lower,
      numericClass.upper,
      numericClass.includeUpper,
      formatBoundary,
    );
    const isExpanded = expandedEntry === key;
    const symbols = effectiveSymbols(layer.style.symbols, numericClass.symbol);
    const exactLower = formatGraduatedNumber(numericClass.lower);
    const exactUpper = formatGraduatedNumber(numericClass.upper);
    return (
      <div className={`thematic-entry${isExpanded ? ' thematic-entry-expanded' : ''}`} key={key}>
        <div className="thematic-row graduated-class-row">
          <button
            aria-expanded={isExpanded}
            aria-label={`Edit symbol for ${numericClass.symbol.legendLabel ?? label}`}
            className="symbol-preview-button"
            onClick={() => setExpandedEntry(isExpanded ? null : key)}
            type="button"
          >
            <SymbolPreview geometry={geometry} symbols={symbols} sample={samples[index]!} />
          </button>
          <div className="graduated-class-label">
            {numericClass.symbol.legendLabel ? (
              <span className="thematic-label">{numericClass.symbol.legendLabel}</span>
            ) : null}
            <div
              className={`graduated-range${numericClass.symbol.legendLabel ? ' graduated-range-generated' : ''}`}
              title={label}
            >
              <span
                className="graduated-range-value graduated-range-lower"
                data-fixed-endpoint={index === 0 ? 'minimum' : undefined}
                title={exactLower}
              >
                {formatBoundary(numericClass.lower)}
              </span>
              <span className="graduated-range-separator" aria-hidden="true">
                {numericClass.includeUpper ? '–' : '– <'}
              </span>
              {numericClass.includeUpper ? (
                <span
                  className="graduated-range-value graduated-range-fixed"
                  data-fixed-endpoint="maximum"
                  title={exactUpper}
                >
                  {formatBoundary(numericClass.upper)}
                </span>
              ) : (
                <BoundaryControl
                  format={formatBoundary}
                  index={index + 1}
                  key={`${layer.id}-${index}-${numericClass.upper}`}
                  layerId={layer.id}
                  onCommit={(boundary) => {
                    const result = setGraduatedBoundary(layer.id, index + 1, boundary);
                    onResult(result);
                    return result.ok;
                  }}
                  value={numericClass.upper}
                />
              )}
            </div>
          </div>
          {renderLabelEditor(key, label, numericClass.symbol.legendLabel, index)}
        </div>
        {isExpanded ? (
          <SymbolOverrideEditor
            families={families}
            target={{ kind: 'point', layerId: layer.id, entry: index }}
            id={`${layer.id}-${key}`}
            onChange={(patch) => onResult(updateClassOverride(layer.id, index, patch))}
            onReset={() => onResult(resetClassOverride(layer.id, index))}
            symbols={symbols}
          />
        ) : null}
      </div>
    );
  };

  return (
    <>
      <section className="inspector-section thematic-settings">
        <div className="style-field">
          <label htmlFor={`classification-field-${layer.id}`}>Attribute field</label>
          <select
            id={`classification-field-${layer.id}`}
            onChange={(event) => onResult(setClassificationField(layer.id, event.target.value))}
            value={classification.field}
          >
            {fields.map((field) => (
              <option key={field.name} value={field.name}>
                {fieldDisplayLabel(field)}
              </option>
            ))}
          </select>
        </div>

        {classification.mode === 'graduated' ? (
          <>
            <div className="style-field style-field-inline">
              <label htmlFor={`graduated-vary-${layer.id}`}>Vary by</label>
              <select
                disabled={families.length !== 1 || families[0] === 'polygon'}
                id={`graduated-vary-${layer.id}`}
                onChange={(event) =>
                  onResult(
                    setGraduatedVisualVariable(
                      layer.id,
                      variationForSelection(event.target.value, classification.visualVariable),
                    ),
                  )
                }
                value={graduatedVariationValue(classification.visualVariable)}
              >
                <option value="colour">Colour</option>
                {families[0] === 'point' && families.length === 1 ? (
                  <>
                    <option value="size">Size</option>
                    <option value="colour-size">Colour &amp; size</option>
                  </>
                ) : null}
                {families[0] === 'line' && families.length === 1 ? (
                  <>
                    <option value="width">Width</option>
                    <option value="colour-width">Colour &amp; width</option>
                  </>
                ) : null}
              </select>
            </div>
            {families.length !== 1 ? (
              <p className="style-help">Mixed-geometry layers vary by colour consistently.</p>
            ) : null}
            <div className="style-field style-field-inline">
              <label htmlFor={`class-count-${layer.id}`}>Classes</label>
              <select
                id={`class-count-${layer.id}`}
                onChange={(event) => onResult(setClassCount(layer.id, Number(event.target.value)))}
                value={classification.classCount}
              >
                {[3, 4, 5, 6, 7].map((count) => (
                  <option key={count} value={count}>
                    {count}
                  </option>
                ))}
              </select>
            </div>
            <div className="style-field style-field-inline">
              <label htmlFor={`classification-method-${layer.id}`}>Method</label>
              <select
                id={`classification-method-${layer.id}`}
                onChange={(event) =>
                  onResult(
                    setGraduatedMethod(layer.id, event.target.value as GraduatedGeneratedMethod),
                  )
                }
                value={classification.method}
              >
                <option value="equal-interval">Equal interval</option>
                <option value="quantile">Quantile</option>
                {classification.method === 'manual' ? (
                  <option value="manual" disabled>
                    Manual
                  </option>
                ) : null}
              </select>
            </div>
            {classification.classes.length < classification.classCount ? (
              <p className="style-help class-count-explanation" role="status">
                {classification.classes.length} classes generated. {classification.classCount}{' '}
                requested;{' '}
                {classification.resetMethod === 'quantile'
                  ? 'repeated values prevent additional quantile breaks.'
                  : 'the available distinct values prevent additional equal-interval classes.'}
              </p>
            ) : null}
            {classification.method === 'manual' ? (
              <button
                className="secondary-button reset-breaks-button"
                onClick={() => onResult(resetGraduatedBreaks(layer.id))}
                type="button"
              >
                Reset using{' '}
                {classification.resetMethod === 'quantile' ? 'Quantile' : 'Equal interval'}
              </button>
            ) : null}
          </>
        ) : null}

        {classification.mode === 'categorised' || colourVariationActive(classification) ? (
          <div className="style-field palette-field">
            <div className="palette-selector-row">
              <PalettePicker
                groups={paletteGroups}
                onSelect={(paletteId) => onResult(setPalette(layer.id, paletteId))}
                selectedColours={thematicColours}
                selectedId={classification.paletteId}
              />
              {classification.mode === 'graduated' ? (
                <button
                  aria-label="Reverse palette"
                  aria-pressed={classification.paletteReversed}
                  className="icon-button reverse-palette-button"
                  onClick={() =>
                    onResult(setGraduatedPaletteReversed(layer.id, !classification.paletteReversed))
                  }
                  title="Reverse palette"
                  type="button"
                >
                  <Icon name="reverse" />
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        {sizeVariable ? (
          <div className="graduated-range-controls">
            <NumericControl
              id={`graduated-minimum-${layer.id}`}
              label={sizeVariable.kind === 'point-size' ? 'Minimum size' : 'Minimum width'}
              max={sizeVariable.kind === 'point-size' ? 40 : 30}
              min={sizeVariable.kind === 'point-size' ? 1 : 0.5}
              onChange={(minimum) =>
                onResult(
                  setGraduatedVisualVariable(layer.id, {
                    ...sizeVariable,
                    minimum: Math.min(minimum, sizeVariable.maximum),
                  }),
                )
              }
              step={0.5}
              suffix="px"
              value={sizeVariable.minimum}
            />
            <NumericControl
              id={`graduated-maximum-${layer.id}`}
              label={sizeVariable.kind === 'point-size' ? 'Maximum size' : 'Maximum width'}
              max={sizeVariable.kind === 'point-size' ? 40 : 30}
              min={sizeVariable.kind === 'point-size' ? 1 : 0.5}
              onChange={(maximum) =>
                onResult(
                  setGraduatedVisualVariable(layer.id, {
                    ...sizeVariable,
                    maximum: Math.max(maximum, sizeVariable.minimum),
                  }),
                )
              }
              step={0.5}
              suffix="px"
              value={sizeVariable.maximum}
            />
          </div>
        ) : null}

        {classification.mode === 'categorised' ? (
          <p className="style-help">
            Up to {MAX_CATEGORISED_VALUES} distinct values are generated automatically.
          </p>
        ) : null}
      </section>

      {classification.mode === 'categorised' ? (
        <section className="inspector-section">
          <div className="style-section-heading">
            <h3>Categories</h3>
            <span>{classification.categories.length}</span>
          </div>
          <div className="thematic-row-list" aria-label="Categorised values">
            {classification.categories.map((category, index) => {
              const label = categoryLabel(category.value);
              return renderEntry(
                `category-${index}`,
                label,
                category.symbol,
                index,
                (patch) => onResult(updateCategoryOverride(layer.id, index, patch)),
                () => onResult(resetCategoryOverride(layer.id, index)),
              );
            })}
            {renderEntry(
              'category-other',
              'Other / unmatched',
              classification.other,
              'other',
              (patch) => onResult(updateCategoryOverride(layer.id, 'other', patch)),
              () => onResult(resetCategoryOverride(layer.id, 'other')),
            )}
          </div>
        </section>
      ) : (
        <section className="inspector-section">
          <div className="style-section-heading">
            <h3>Classes</h3>
            <span>{classification.classes.length}</span>
          </div>
          <p className="style-help class-boundary-help">
            Edit an internal boundary value to use manual breaks.
          </p>
          <div className="thematic-row-list" aria-label="Graduated classes">
            {numericClasses.map((numericClass, index) => renderGraduatedEntry(index, numericClass))}
            {renderEntry(
              'class-null',
              'Null / non-numeric',
              classification.nullSymbol,
              'null',
              (patch) => onResult(updateClassOverride(layer.id, 'null', patch)),
              () => onResult(resetClassOverride(layer.id, 'null')),
            )}
          </div>
        </section>
      )}
    </>
  );
}
